import {
  CaregiverRelationshipKind,
  HousingOwnership,
  ResidenceType,
  VehicleOwnership,
} from '../../__generated__/globalTypes';

/** A selectable option backed by a domain enum, shaped for `Select` dropdowns. */
export interface ILabeledOption<T> {
  id: T;
  label: string;
}

/**
 * Selectable caregiver relationships. `OTHER` is intentionally excluded — it
 * needs a free-text companion field (backend DB CHECK) that isn't built yet.
 * Shared by the onboarding loved-ones flow and the dependents settings form.
 */
export const RELATIONSHIP_OPTIONS: ILabeledOption<CaregiverRelationshipKind>[] = [
  { id: CaregiverRelationshipKind.Mother, label: 'Mother' },
  { id: CaregiverRelationshipKind.Father, label: 'Father' },
  { id: CaregiverRelationshipKind.Grandmother, label: 'Grandmother' },
  { id: CaregiverRelationshipKind.Grandfather, label: 'Grandfather' },
  { id: CaregiverRelationshipKind.Aunt, label: 'Aunt' },
  { id: CaregiverRelationshipKind.Uncle, label: 'Uncle' },
];

/**
 * Display labels for every relationship kind, including `OTHER` (display-only).
 * Derived from `RELATIONSHIP_OPTIONS` so the selectable labels live in one place.
 */
export const RELATIONSHIP_LABELS: Record<string, string> = {
  ...Object.fromEntries(RELATIONSHIP_OPTIONS.map((o) => [o.id, o.label])),
  [CaregiverRelationshipKind.Other]: 'Other',
};

/**
 * Living-situation residence kinds captured for a dependant. Shared by the
 * onboarding residence step and the dependents settings form.
 * `FREQUENT_LOCATION` is deliberately absent — it marks family-shared
 * frequent-location rows, never a member's living situation, and the backend
 * rejects it on a primary residence.
 */
export const RESIDENCE_TYPE_OPTIONS: ILabeledOption<ResidenceType>[] = [
  { id: ResidenceType.IndependentLiving, label: 'Living independently (at home)' },
  { id: ResidenceType.FamilyMember, label: 'Living with family member' },
  { id: ResidenceType.AssistedLiving, label: 'Living in nursing home or assisted living facility' },
  { id: ResidenceType.Other, label: 'Other' },
];

/**
 * Vehicle ownership kinds offered when assigning a vehicle. `OTHER` is excluded
 * (it needs a free-text companion the vehicle UI doesn't collect). Shared by the
 * onboarding vehicle step and the Possessions settings vehicle card.
 */
export const VEHICLE_OWNERSHIP_OPTIONS: ILabeledOption<VehicleOwnership>[] = [
  { id: VehicleOwnership.OwnOutright, label: 'Own outright' },
  { id: VehicleOwnership.Loan, label: 'Car loan' },
  { id: VehicleOwnership.Lease, label: 'Lease' },
];

/**
 * Home-ownership options shown for an independently-living dependant's
 * residence. Co-located with the other residence option lists for reuse.
 */
export const HOME_OWNERSHIP_OPTIONS: ILabeledOption<HousingOwnership>[] = [
  { id: HousingOwnership.OwnOutright, label: 'Own outright' },
  { id: HousingOwnership.Mortgage, label: 'Own with mortgage' },
  { id: HousingOwnership.Rent, label: 'Rent' },
  { id: HousingOwnership.Other, label: 'Other' },
];
